import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useFolderUpdateName } from 'src/features/folders/hooks';
import { letterMatchingRegex } from 'src/helpers/formRegex';
import { EntryStatusEnum } from 'src/ts/enums';
import { ICompany, IEntry, IFolder } from 'src/ts/interfaces';
import {
  AddressContainer,
  AddressDetails,
  AddressInfo,
  AddressName,
  AddressNameSection,
  ContentSection,
  CustomButton,
  DocumentsText,
  EditButton,
  EditFormContainer,
  FolderHeader,
  FolderInfo,
  FolderInfoSection,
  FolderInfoText,
  FolderName,
  FolderNameSection,
  FolderSection,
  FolderTimestamp,
  IconContainer,
  IconText,
  LeftPanel,
  LeftPanelContent,
  NotificationBadge,
  PotentialMatchesButton,
  ReviewReportContainer,
  ReviewTitle,
  StatusContainer,
  StyledIconButtonBlue,
  StyledIconButtonRed,
  StyledInputText
} from '../styles';

interface LeftPanelDetailsProps {
  entry: IEntry;
  folder: IFolder | null;
  currentManualIndex: number;
  manualProofCount: number;
  propertyData?: {
    name?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  companyData?: ICompany;
  hasMatches?: boolean;
  matchesCount?: number;
  showPotentialMatches: () => void;
  onReviewReport: () => void;
  isSubmissionReviewMode?: boolean;
  hideReviewReport?: boolean;
}

const LeftPanelDetails: FC<LeftPanelDetailsProps> = ({
  entry,
  folder,
  currentManualIndex,
  manualProofCount,
  propertyData,
  companyData,
  onReviewReport,
  hasMatches,
  matchesCount,
  showPotentialMatches,
  isSubmissionReviewMode,
  hideReviewReport
}) => {
  const { onUpdateName } = useFolderUpdateName();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const formProps = useForm({
    mode: 'onChange',
    defaultValues: {
      folderName: folder?.name
    }
  });

  const { handleSubmit, watch, setValue } = formProps;

  // Update form when folder data changes
  useEffect(() => {
    if (folder?.name) {
      setValue('folderName', folder.name);
    }
  }, [folder?.name, setValue]);

  const isSubmitDisabled = folder?.name === watch('folderName') || !watch('folderName');

  const onSubmit = useCallback(
    ({ folderName }: any) => {
      setIsEditMode(false);
      onUpdateName({ id: folder?.id as string, name: folderName });
    },
    [folder?.id, onUpdateName]
  );

  const onCancel = useCallback(() => {
    setValue('folderName', folder?.name);
    setIsEditMode(false);
  }, [folder?.name, setValue]);

  const handleCopyId = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(folder?.id || '');
    } catch (err) {
      console.error('Failed to copy ID to clipboard:', err);
    }
  }, [folder?.id]);

  const getStatusText = (status: string) => {
    switch (status) {
      case EntryStatusEnum.Pending:
        return 'Ready for Review';
      case EntryStatusEnum.Ready:
        return 'Review Complete';
      case EntryStatusEnum.Viewed:
        return 'Report Viewed';
      default:
        return 'Ready for Review';
    }
  };

  const formatEntryTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return `${date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    })} at ${date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })}`;
  };

  return (
    <FormProvider {...formProps}>
      <LeftPanel>
        <LeftPanelContent>
          <ContentSection>
            <StatusContainer>
              <ReviewTitle>{getStatusText(entry.status)}</ReviewTitle>
            </StatusContainer>
            {!isSubmissionReviewMode && (
              <DocumentsText>
                Document {currentManualIndex + 1} of {manualProofCount}
              </DocumentsText>
            )}
          </ContentSection>

          <FolderSection>
            <FolderHeader>
              <FolderNameSection>
                <IconContainer>
                  <IconText>
                    <img src="/assets/images/folder-document.svg" alt="Folder" />
                  </IconText>
                </IconContainer>
                {isEditMode ? (
                  <EditFormContainer>
                    <StyledInputText
                      name="folderName"
                      placeholder="Folder Name"
                      config={{
                        required: 'Complete this field',
                        maxLength: {
                          value: 50,
                          message: '50 Characters Maximum'
                        },
                        validate: {
                          noSpecialCharacters: (value) =>
                            letterMatchingRegex.test(value) ||
                            'Should not contain only numbers and symbols'
                        }
                      }}
                    />
                    <StyledIconButtonBlue
                      name="confirm-folder-name"
                      background="none"
                      icon="check"
                      iconColor={isSubmitDisabled ? 'gray600' : 'blue700'}
                      disabled={isSubmitDisabled}
                      onClick={handleSubmit(onSubmit)}
                      isCircle={false}
                    />
                    <StyledIconButtonRed
                      name="cancel-folder-name"
                      background="none"
                      icon="close"
                      iconColor="red"
                      disabled={false}
                      isCircle={false}
                      onClick={onCancel}
                    />
                  </EditFormContainer>
                ) : (
                  <FolderName>{folder?.name}</FolderName>
                )}
              </FolderNameSection>
              {!isEditMode && <EditButton onClick={() => setIsEditMode(true)}>Edit</EditButton>}
            </FolderHeader>

            <FolderInfo>
              <FolderInfoSection>
                <IconContainer>
                  <img src="/assets/images/user-profile.svg" alt="Folder Creation" />
                </IconContainer>
                <div>
                  <FolderInfoText>{formatEntryTimestamp(folder?.inserted_at || '')}</FolderInfoText>
                  <FolderTimestamp onClick={handleCopyId}>#{folder?.id}</FolderTimestamp>
                </div>
              </FolderInfoSection>
            </FolderInfo>

            <FolderInfo>
              <FolderInfoSection>
                <IconContainer>
                  <img src="/assets/images/property-document.svg" alt="Company" />
                </IconContainer>
                <div>
                  <FolderInfoText>{companyData?.name || 'Company Name'}</FolderInfoText>
                </div>
              </FolderInfoSection>
            </FolderInfo>

            <AddressInfo>
              <AddressNameSection>
                <IconContainer>
                  <img src="/assets/images/location-pin.svg" alt="Location" />
                </IconContainer>
                <AddressContainer>
                  <AddressName>{folder?.property?.name || 'Property Address'}</AddressName>
                  <AddressDetails>
                    {propertyData?.address || 'Address not available'}
                    {propertyData?.city && propertyData?.state && (
                      <>
                        <br />
                        {propertyData.city}, {propertyData.state} {propertyData.zip}
                      </>
                    )}
                  </AddressDetails>
                </AddressContainer>
              </AddressNameSection>
            </AddressInfo>
          </FolderSection>

          {hasMatches && (
            <PotentialMatchesButton onClick={() => showPotentialMatches()}>
              Potential Matches
              {matchesCount && matchesCount > 0 && (
                <NotificationBadge>{matchesCount}</NotificationBadge>
              )}
            </PotentialMatchesButton>
          )}

          {!hideReviewReport && (
            <ReviewReportContainer>
              <CustomButton onClick={onReviewReport}>Review submission</CustomButton>
            </ReviewReportContainer>
          )}
        </LeftPanelContent>
      </LeftPanel>
    </FormProvider>
  );
};

export default LeftPanelDetails;
